/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useMemo, useRef } from 'react';
import { Job, Utils } from 'ogc-processes-components';
import { classNames } from 'primereact/utils';
import { Skeleton } from 'primereact/skeleton';
import { LazyLog, ScrollFollow } from '@melloware/react-logviewer';
import { useTranslation } from 'react-i18next';
import { useAuthService } from '@/hooks/useAuthService';

interface Props {
  job: Job;
  selectedLogId: string | undefined;
  findLogBynameFn: (logArray: string[], logName: string) => string | undefined;
  fallbackLogHref: string | undefined;
  className?: string;
}

export const LogsViewer: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { className, job, selectedLogId, findLogBynameFn, fallbackLogHref } = props;
  const { getValidToken } = useAuthService();
  const fullscreen = useRef(false);

  const [token, setToken] = React.useState<string | null>(null);

  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getValidToken().then((validToken) => {
      setToken(validToken);
    });
  }, [getValidToken]);

  const selectedLogHref = useMemo(() => {
    if (selectedLogId) {
      const _selectedLogHref = findLogBynameFn(job.log_urls ?? [], selectedLogId ?? '');
      return _selectedLogHref;
    } else {
      return fallbackLogHref;
    }
  }, [selectedLogId, findLogBynameFn, job.log_urls, fallbackLogHref]);

  const generateFullScreenBtn = (id: string, hidden?: boolean) => {
    const fullScreenButton = document.createElement('button');
    fullScreenButton.className = 'p-button p-button-icon-only p-button-text p-button-secondary';
    fullScreenButton.id = id;
    fullScreenButton.style.opacity = '0.5';
    fullScreenButton.style.display = hidden ? 'none' : 'block';
    return fullScreenButton;
  };

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) {
      return;
    }

    const injectButtons = (): boolean => {
      const searchBarContainer: HTMLDivElement | null = wrapper.querySelector('.react-lazylog-searchbar');
      if (!searchBarContainer) {
        return false;
      }
      // Buttons already injected for this search bar instance.
      if (searchBarContainer.querySelector('#fullScreenButton')) {
        return true;
      }

      const fullScreenButton = generateFullScreenBtn('fullScreenButton');
      const exitFullScreenButton = generateFullScreenBtn('exitFullScreenButton', true);
      fullScreenButton.onclick = () => {
        Utils.elementToFullscreen(wrapper, () => {
          fullscreen.current = true;
        });
      };
      fullScreenButton.innerHTML = `
    <svg width="20" height="20px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path fill="#ffff" d="M4.75 9.233C4.75 9.64721 5.08579 9.983 5.5 9.983C5.91421 9.983 6.25 9.64721 6.25 9.233H4.75ZM6.25 5.5C6.25 5.08579 5.91421 4.75 5.5 4.75C5.08579 4.75 4.75 5.08579 4.75 5.5H6.25ZM5.5 4.75C5.08579 4.75 4.75 5.08579 4.75 5.5C4.75 5.91421 5.08579 6.25 5.5 6.25V4.75ZM9.233 6.25C9.64721 6.25 9.983 5.91421 9.983 5.5C9.983 5.08579 9.64721 4.75 9.233 4.75V6.25ZM6.03033 4.96967C5.73744 4.67678 5.26256 4.67678 4.96967 4.96967C4.67678 5.26256 4.67678 5.73744 4.96967 6.03033L6.03033 4.96967ZM9.96967 11.0303C10.2626 11.3232 10.7374 11.3232 11.0303 11.0303C11.3232 10.7374 11.3232 10.2626 11.0303 9.96967L9.96967 11.0303ZM15.767 18.75C15.3528 18.75 15.017 19.0858 15.017 19.5C15.017 19.9142 15.3528 20.25 15.767 20.25V18.75ZM19.5 20.25C19.9142 20.25 20.25 19.9142 20.25 19.5C20.25 19.0858 19.9142 18.75 19.5 18.75V20.25ZM18.75 19.5C18.75 19.9142 19.0858 20.25 19.5 20.25C19.9142 20.25 20.25 19.9142 20.25 19.5H18.75ZM20.25 15.767C20.25 15.3528 19.9142 15.017 19.5 15.017C19.0858 15.017 18.75 15.3528 18.75 15.767H20.25ZM18.9697 20.0303C19.2626 20.3232 19.7374 20.3232 20.0303 20.0303C20.3232 19.7374 20.3232 19.2626 20.0303 18.9697L18.9697 20.0303ZM15.0303 13.9697C14.7374 13.6768 14.2626 13.6768 13.9697 13.9697C13.6768 14.2626 13.6768 14.7374 13.9697 15.0303L15.0303 13.9697ZM6.25 15.767C6.25 15.3528 5.91421 15.017 5.5 15.017C5.08579 15.017 4.75 15.3528 4.75 15.767H6.25ZM4.75 19.5C4.75 19.9142 5.08579 20.25 5.5 20.25C5.91421 20.25 6.25 19.9142 6.25 19.5H4.75ZM5.5 18.75C5.08579 18.75 4.75 19.0858 4.75 19.5C4.75 19.9142 5.08579 20.25 5.5 20.25V18.75ZM9.233 20.25C9.64721 20.25 9.983 19.9142 9.983 19.5C9.983 19.0858 9.64721 18.75 9.233 18.75V20.25ZM4.96967 18.9697C4.67678 19.2626 4.67678 19.7374 4.96967 20.0303C5.26256 20.3232 5.73744 20.3232 6.03033 20.0303L4.96967 18.9697ZM11.0303 15.0303C11.3232 14.7374 11.3232 14.2626 11.0303 13.9697C10.7374 13.6768 10.2626 13.6768 9.96967 13.9697L11.0303 15.0303ZM15.767 4.75C15.3528 4.75 15.017 5.08579 15.017 5.5C15.017 5.91421 15.3528 6.25 15.767 6.25V4.75ZM19.5 6.25C19.9142 6.25 20.25 5.91421 20.25 5.5C20.25 5.08579 19.9142 4.75 19.5 4.75V6.25ZM20.25 5.5C20.25 5.08579 19.9142 4.75 19.5 4.75C19.0858 4.75 18.75 5.08579 18.75 5.5H20.25ZM18.75 9.233C18.75 9.64721 19.0858 9.983 19.5 9.983C19.9142 9.983 20.25 9.64721 20.25 9.233H18.75ZM20.0303 6.03033C20.3232 5.73744 20.3232 5.26256 20.0303 4.96967C19.7374 4.67678 19.2626 4.67678 18.9697 4.96967L20.0303 6.03033ZM13.9697 9.96967C13.6768 10.2626 13.6768 10.7374 13.9697 11.0303C14.2626 11.3232 14.7374 11.3232 15.0303 11.0303L13.9697 9.96967ZM6.25 9.233V5.5H4.75V9.233H6.25ZM5.5 6.25H9.233V4.75H5.5V6.25ZM4.96967 6.03033L9.96967 11.0303L11.0303 9.96967L6.03033 4.96967L4.96967 6.03033ZM15.767 20.25H19.5V18.75H15.767V20.25ZM20.25 19.5V15.767H18.75V19.5H20.25ZM20.0303 18.9697L15.0303 13.9697L13.9697 15.0303L18.9697 20.0303L20.0303 18.9697ZM4.75 15.767V19.5H6.25V15.767H4.75ZM5.5 20.25H9.233V18.75H5.5V20.25ZM6.03033 20.0303L11.0303 15.0303L9.96967 13.9697L4.96967 18.9697L6.03033 20.0303ZM15.767 6.25H19.5V4.75H15.767V6.25ZM18.75 5.5V9.233H20.25V5.5H18.75ZM18.9697 4.96967L13.9697 9.96967L15.0303 11.0303L20.0303 6.03033L18.9697 4.96967Z"/>
    </svg>
`;
      exitFullScreenButton.onclick = () => {
        Utils.elementExitFullscreen(document, () => {
          fullscreen.current = false;
        });
      };
      exitFullScreenButton.innerHTML = `
    <svg width="20px" height="20px" viewBox="0 0 16 16" fill="#ffff" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <rect width="16" height="16" id="icon-bound" fill="none" />
      <path d="M13,11H9v2h6V1H3v6h2V3h8V11z M0,8v8h8v-2H3.412l6.294-6.294L8.294,6.294L2,12.587V8H0z" />
    </svg>
`;
      wrapper.addEventListener('fullscreenchange', () => {
        if (fullScreenButton.style.display === 'block') {
          exitFullScreenButton.style.display = 'block';
          fullScreenButton.style.display = 'none';
          return;
        }
        if (exitFullScreenButton.style.display === 'block') {
          fullScreenButton.style.display = 'block';
          exitFullScreenButton.style.display = 'none';
        }
      });

      searchBarContainer.appendChild(fullScreenButton);
      searchBarContainer.appendChild(exitFullScreenButton);
      return true;
    };

    if (injectButtons()) {
      return;
    }

    const observer = new MutationObserver(() => {
      injectButtons();
    });
    observer.observe(wrapper, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [token, selectedLogHref]);

  return (
    <div
      ref={wrapperRef}
      className={classNames(className, 'w-full border-round')}
      style={{ minHeight: 300, height: 700, width: '100%' }}
    >
      <ScrollFollow
        startFollowing={true}
        render={({ follow, onScroll }) => (
          <>
            {selectedLogHref ? (
              token ? (
                <LazyLog
                  enableSearch
                  enableSearchNavigation
                  url={selectedLogHref}
                  fetchOptions={{ headers: { Authorization: `Bearer ${token}` } }}
                  stream
                  selectableLines
                  follow={follow}
                  onScroll={onScroll}
                />
              ) : (
                <Skeleton className={classNames(className, 'w-full border-round')} width="100%" height="700px" />
              )
            ) : (
              <LazyLog
                enableSearch
                enableSearchNavigation
                text={t(['misc.no_log', 'No log found'])}
                height={700}
                selectableLines
                follow={follow}
                onScroll={onScroll}
              />
            )}
          </>
        )}
      />
    </div>
  );
};
